import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/context';
import { CACHE } from '../api/cache';

// Which backend this tenant runs on. A tenant whose overlay is on runs on its connected system
// (HaloPSA): only the resources that system serves have screens, plus NovaPSA's own shell (who
// may sign in, roles, workspace settings, the connection). Everything else is hidden — the API
// answers it with 501 anyway (src/services/overlayService.ts gate). Read once per session from
// GET /v1/integrations/overlay/session; any error resolves to "NovaPSA" (show everything) so the
// shell never blocks on it, and the API stays the real gate.

export interface OverlayInfo {
  integration: string;
  resources: string[];
  /** Resources NovaPSA still answers itself (its shell); absent from an older API = do not gate. */
  local?: string[];
  /** The connected system's own UI: home, configuration area, and per-resource pages (null when unknown). */
  app?: { url: string; config_url: string; links: Record<string, string> } | null;
}

const SYSTEM_NAME: Record<string, string> = { halo: 'Halo' };
/** The connected system's display name ("Halo"). */
export const systemName = (overlay: OverlayInfo): string => SYSTEM_NAME[overlay.integration] ?? overlay.integration;

/** Open a page of the connected system's own UI in a new tab. */
export const openExternal = (url: string): void => {
  window.open(url, '_blank', 'noopener,noreferrer');
};
interface SessionInfo {
  signed_in_with_halo: boolean;
  overlay?: OverlayInfo | null;
}

export const OVERLAY_SESSION_KEY = ['integrations', 'overlay', 'session'] as const;

/** First path segments of NovaPSA shell screens, available whatever the backend. */
const SHELL_ROUTES = new Set(['settings', 'integrations', 'team', 'invites', 'roles', 'about', 'feedback']);
/** Screens whose route is not the resource name. */
const ROUTE_RESOURCE: Record<string, string> = { servicedesk: 'ticket-types', dashboards: 'report-dashboards' };

const segmentOf = (to: string): string => to.split(/[?#]/)[0]!.split('/').filter(Boolean)[0] ?? '';

/** Is a screen at `to` backed for a tenant on this overlay (null = runs on NovaPSA: everything)? */
export function routeServed(overlay: OverlayInfo | null | undefined, to: string): boolean {
  if (!overlay) return true;
  const seg = segmentOf(to);
  if (!seg || SHELL_ROUTES.has(seg)) return true;
  return overlay.resources.includes(ROUTE_RESOURCE[seg] ?? seg);
}

/**
 * Will the API answer this resource for the tenant? False only once we KNOW the tenant runs on a
 * connected system that does not serve it and NovaPSA does not keep it local — then asking is a
 * certain 501, so the engine hooks do not ask. Unknown (session still loading, older API) = yes.
 */
export function resourceServed(overlay: OverlayInfo | null | undefined, name: string): boolean {
  if (!overlay || !overlay.local) return true;
  return overlay.resources.includes(name) || overlay.local.includes(name);
}

export function useDataSource(): { overlay: OverlayInfo | null | undefined; isServed: (to: string) => boolean; resourceServed: (name: string) => boolean } {
  const api = useApi();
  const { data } = useQuery<SessionInfo | null>({
    queryKey: [...OVERLAY_SESSION_KEY, api.prefix],
    ...CACHE.session,
    retry: false,
    queryFn: async () => {
      try {
        return await api.request<SessionInfo>(`${api.prefix}/integrations/overlay/session`);
      } catch {
        return null; // admin surface / error → treat as NovaPSA
      }
    },
  });
  const overlay = data === undefined ? undefined : (data?.overlay ?? null);
  return { overlay, isServed: (to: string) => routeServed(overlay, to), resourceServed: (name: string) => resourceServed(overlay, name) };
}
